// @flow

import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { getFacetsAsString } from 'Components/widgets/facet-search/FacetSearch.utils';
import {
  facetingCriteriaSelector,
  filteredProductsSelector,
  filteredProductsCountSelector,
} from 'Store/faceting/faceting.selector';
import { keywordsSelector } from 'Store/search/search.selector';
import {
  getDepartureDate,
  getTripDuration,
  getDepartureCity,
} from 'Store/trip-criteria/tripCriteria.selector';
import { getKids, getAdults } from 'Store/trip-information/storeHelper';
import { type Adults } from 'Store/trip-information/adults/adults.reducer';
import { type Kids } from 'Store/trip-information/kids/kids.reducer';
import { VIRTUAL_PAGE_LOAD } from 'Tracking/constants';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { COUNTRIES_FACET_ID, DATE_FORMAT, DESTINATIONS_FACET_ID } from 'Utils/faceting/constants';
import { isSummer } from 'Utils/tracking/resort';

type TrackingSearchResort = {
  resortCode: string,
  resortName: string,
  resortTrident: number,
  resortCountry: ?string,
  resortRegion: ?string,
  resortAdult: ?number,
  resortChildren: number,
  resortDepartureCityCode: ?string,
  resortSeasonType: string,
  resortUrlImage: ?string,
  versionBooking: string,
  resortSeason: 'Summer' | 'Winter',
  resortYearSeason: string,
};

type Payload = {
  event: string,
  internalSearchEngineNbResults: 'redirection' | number,
  pageName: string,
  resortAdult: number,
  resortChildren: number,
  resortDepartureCityCode: string,
  resortNightNumber: number,
  resortTotalParticipant: number,
  resortTransport: string,
  searchDate: string,
  searchDepartureDate: string,
  searchDestinationArea: string,
  searchDestinationCountry: string,
  searchRequestType: 'Autocomplete' | 'Nocomplete',
  searchTerms: string,
  siteSection: string,
  searchResorts: Array<TrackingSearchResort>,
};

export const useTrackSearchResults = () => {
  const track = useTracking();
  const adults: Array<Adults> = useSelector(state => getAdults(state) || []);
  const departureCity: ?string = useSelector(state => getDepartureCity(state));
  const departureDate: ?number = useSelector(state => getDepartureDate(state));
  const duration: ?number = useSelector(state => getTripDuration(state));
  const { filters = [] } = useSelector(state => facetingCriteriaSelector(state));
  const keywords: ?string = useSelector(state => keywordsSelector(state));
  const kids: Array<Kids> = useSelector(state => getKids(state) || []);
  const products: Array<Resort> = useSelector(state => filteredProductsSelector(state) || []);
  const productsCount: number = useSelector(state => filteredProductsCountSelector(state) || 0);
  const query: SearchQuery = useSelector(({ faceting }) => faceting.query || {});
  const redirection: ?string = useSelector(({ faceting }) => faceting.redirection);

  const isFromAutoComplete = Boolean(query.type);
  const nbAdults = adults.length;
  const nbKids = kids.length;
  const nbParticipants = nbAdults + nbKids;
  const searchTerms = isFromAutoComplete || query.label ? query.label : keywords;
  const withFilter = Boolean(
    nbKids > 0 || duration || departureDate || departureCity || filters.length > 0,
  );

  const executeTracking = (payload?: $Shape<Payload>) => {
    const searchResorts: Array<TrackingSearchResort> = products.map<TrackingSearchResort>(
      product => ({
        resortCode: product.id,
        resortName: product.name,
        resortTrident: product.comfort,
        resortCountry: product.destination ? product.destination.country : null,
        resortRegion: product.destination ? product.destination.area : null,
        resortAdult: nbAdults,
        resortChildren: nbKids,
        resortDepartureCityCode: departureCity,
        resortSeasonType: product.seasonName || 'Summer',
        resortUrlImage: product.image,
        versionBooking: 'responsive',
        resortSeason: departureDate && isSummer(new Date(departureDate)) ? 'Summer' : 'Winter',
        resortYearSeason: moment(departureDate).format('YYYY'),
      }),
    );

    // $FlowFixMe
    track({
      event: VIRTUAL_PAGE_LOAD,
      internalSearchEngineNbResults: redirection ? 'redirection' : productsCount,
      pageName: 'shopping_home_search',
      resortAdult: nbAdults,
      resortChildren: nbKids,
      resortDepartureCityCode: departureCity,
      resortNightNumber: duration,
      resortTotalParticipant: nbParticipants,
      resortTransport: departureCity ? 'with Transport' : null,
      searchDate: moment().format(DATE_FORMAT),
      searchDepartureDate: departureDate ? moment(departureDate).format(DATE_FORMAT) : null,
      searchDestinationArea: getFacetsAsString(filters, DESTINATIONS_FACET_ID),
      searchDestinationCountry: getFacetsAsString(filters, COUNTRIES_FACET_ID),
      searchFilter: withFilter ? 'withFilter' : 'withoutFilter',
      searchRequestType: isFromAutoComplete ? 'Autocomplete' : 'Nocomplete',
      searchTerms: searchTerms || '',
      // $FlowFixMe - Could not get around an issue with Array<TrackingSearchResort>
      searchResorts,
      ...payload,
    });

    // Reset searchReferrer after each pageView is sent.
    // If searchReferrer === "null", GTM will set it to "search"
    track({
      event: 'PUSH',
      searchReferrer: 'null',
    });
  };

  return executeTracking;
};
